// SceneryTileEnumeration — pure nearest-first ring tile enumeration (the Set15.5 scenery-invisible fix; see repo-memory note).
import {tileSizeForMpp} from "../@SceneryBudgetConfig.js";

/** Enumerate the tiles to place. 3D/perspective: rings outward from the eye (nearest-first, degrades by dropping farthest, never silently zero), frustum-reject off-screen tiles, per-ring tile size (~128 m near → 2048 m far). 2D/ortho: the unchanged rect walk. Pure so tests can assert it. */
export function enumerateSceneryTiles(opts: {
	isPerspective: boolean;
	sceneryMaxDistance: number;
	vp: {minX: number; minY: number; maxX: number; maxY: number};
	mpp: number;
	cameraPos: {x: number; y: number};
	cameraForward: {x: number; y: number};
	cameraHFovHalfAngle: number;
	mppAt: (x: number, y: number)=>number;
	maxTiles?: number;
}): {minX: number; minY: number; size: number}[] {
	const {isPerspective, sceneryMaxDistance, vp, mpp, cameraPos, cameraForward, cameraHFovHalfAngle, mppAt} = opts;
	const maxTiles = opts.maxTiles ?? 1500;
	const maxDistSq = sceneryMaxDistance * sceneryMaxDistance;
	const tileKeys: {minX: number; minY: number; size: number}[] = [];
	if (isPerspective && maxDistSq > 0) {
		// The horizontal FOV bounds how far off-axis a tile may sit before we stop spending budget
		// on it. Tiles within `sceneryMaxDistance` but outside this wedge are behind/off-screen.
		const fovHalf = Math.max(cameraHFovHalfAngle, 1e-3);
		// A degenerate (straight-down) camera has no ground-plane forward — skip the wedge reject
		// entirely (full circle) so it doesn't drop every tile.
		const hasForward = !(cameraForward.x == 0 && cameraForward.y == 0);
		// Rings: step `ringSize` outward from the eye. Within a ring the tile size is constant
		// (computed at the ring's inner distance), so all tiles in a ring share the same size — the
		// per-ring tile-size variant of Dial 2.
		for (let ringDist = 0; ringDist <= sceneryMaxDistance && tileKeys.length < maxTiles; ) {
			const ringSize = tileSizeForMpp(mppAt(cameraPos.x + cameraForward.x * ringDist, cameraPos.y + cameraForward.y * ringDist));
			// The ring's cell extent (a ring is one `ringSize` thick, centred at ringDist).
			const outer = ringDist + ringSize;
			const c0 = Math.floor((cameraPos.x - outer) / ringSize), c1 = Math.floor((cameraPos.x + outer) / ringSize);
			const r0 = Math.floor((cameraPos.y - outer) / ringSize), r1 = Math.floor((cameraPos.y + outer) / ringSize);
			// Emit the ring's BORDER cells (the square annulus): interior cells were covered by inner
			// rings. EXCEPT ring 0 — the innermost ring has no inner ring, so its interior (the cell
			// containing the eye itself) must be emitted too, or the exact-eye cell is never placed.
			for (let cy = r0; cy <= r1 && tileKeys.length < maxTiles; cy++) {
				for (let cx = c0; cx <= c1 && tileKeys.length < maxTiles; cx++) {
					const onBorder = ringDist == 0 || cx == c0 || cx == c1 || cy == r0 || cy == r1;
					if (!onBorder) continue;
					const minX = cx * ringSize, minY = cy * ringSize;
					const ccx = minX + ringSize / 2, ccy = minY + ringSize / 2;
					const dcx = ccx - cameraPos.x, dcy = ccy - cameraPos.y;
					const d2 = dcx * dcx + dcy * dcy;
					if (d2 > maxDistSq) continue; // outside the draw disc
					// Reject only tiles ENTIRELY outside the FOV wedge: a close tile subtends a huge
					// angle, so its centre can sit outside the wedge while it fills the view — inflate
					// by the tile's angular half-extent (far tiles: ~the plain centre-point wedge).
					if (hasForward) {
						const d = Math.sqrt(d2);
						const halfDiag = ringSize * 0.71; // half diagonal ≈ 0.71·size
						const angHalf = d > 1e-6 ? Math.atan(halfDiag / d) : Math.PI / 2;
						const dotMin = Math.cos(fovHalf * 1.05 + angHalf);
						const dot = (dcx * cameraForward.x + dcy * cameraForward.y) / d;
						if (dot < dotMin) continue;
					}
					tileKeys.push({minX, minY, size: ringSize});
				}
			}
			// Advance by the ring's own size so the annulus thickness matches the tile size at this
			// distance (a variable-stride ring sweep, unlike the fixed-stride row-major walk).
			ringDist += ringSize;
		}
	} else {
		const tileSize = tileSizeForMpp(mpp);
		const t0x = Math.floor(vp.minX / tileSize);
		const t0y = Math.floor(vp.minY / tileSize);
		const t1x = Math.floor((vp.maxX - 1e-6) / tileSize);
		const t1y = Math.floor((vp.maxY - 1e-6) / tileSize);
		for (let ty = t0y; ty <= t1y && tileKeys.length < maxTiles; ty++) {
			for (let tx = t0x; tx <= t1x && tileKeys.length < maxTiles; tx++) {
				const minX = tx * tileSize, minY = ty * tileSize;
				// 2D never skips by distance (sceneryMaxDistance=0 → the guard below is off).
				if (maxDistSq > 0) {
					const dcx = minX + tileSize / 2 - cameraPos.x;
					const dcy = minY + tileSize / 2 - cameraPos.y;
					if (dcx * dcx + dcy * dcy > maxDistSq) continue;
				}
				tileKeys.push({minX, minY, size: tileSize});
			}
		}
	}
	return tileKeys;
}
